/**
 * Lazy PostHog singleton.
 *
 * Defers the 181 KB posthog-js bundle out of the critical render path.
 * Call `initPosthog()` at idle time; use `getPosthogSync()` for fire-and-forget
 * calls that gracefully degrade to no-ops when not yet loaded.
 */

type PostHogInstance = import("posthog-js").PostHog;

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ||
  "https://us.i.posthog.com";

let instance: PostHogInstance | null = null;
let initPromise: Promise<PostHogInstance | null> | null = null;

/**
 * Dynamically imports and initializes PostHog. Safe to call multiple times —
 * subsequent calls return the same promise/instance.
 */
export function initPosthog(): Promise<PostHogInstance | null> {
  if (initPromise) return initPromise;

  if (!POSTHOG_KEY) {
    initPromise = Promise.resolve(null);
    return initPromise;
  }

  initPromise = import("posthog-js").then((mod) => {
    const ph = mod.default;
    ph.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      autocapture: true,
      capture_exceptions: true,
      session_recording: { recordCrossOriginIframes: false },
      loaded: () => {
        if (import.meta.env.DEV) ph.debug();
      },
    });
    instance = ph;
    return ph;
  });

  return initPromise;
}

/**
 * Returns the PostHog instance synchronously, or `null` if not yet loaded.
 * Use for fire-and-forget calls (tracking, identify, reset) that are fine
 * being silently skipped during the first few seconds of page load.
 */
export function getPosthogSync(): PostHogInstance | null {
  return instance;
}
